/**
 * Product Card Image Slider
 * Gestisce lo scorrimento delle immagini prodotto nelle card delle collezioni
 * Include supporto touch/swipe per mobile
 */

(function() {
  'use strict';

  // Stato globale per tracciare l'indice corrente per ogni card
  const cardStates = new Map();

  // Configurazione swipe
  const SWIPE_THRESHOLD = 50; // pixel minimi per considerare uno swipe

  // Preload immagine e restituisce una Promise
  function preloadImage(src) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = src;
      // Se già in cache, resolve immediatamente
      if (img.complete) resolve(true);
    });
  }

  function initProductCardSlider() {
    // Seleziona tutti i container di navigazione
    const navContainers = document.querySelectorAll('.product-card-nav');

    navContainers.forEach(nav => {
      if (nav.dataset.initialized) return;
      nav.dataset.initialized = 'true';

      const productId = nav.dataset.productId;
      const totalImages = parseInt(nav.dataset.totalImages, 10);
      const mediaContainer = nav.closest('.product__media');
      const productInner = nav.closest('.product__inner');

      // Trova il JSON con le immagini
      const jsonScript = mediaContainer?.querySelector('.product-media-json');
      if (!jsonScript) return;

      let mediaData;
      try {
        mediaData = JSON.parse(jsonScript.textContent);
      } catch (e) {
        console.error('Error parsing media JSON:', e);
        return;
      }

      if (mediaData.length < 2) return;

      // Inizializza stato - partiamo da indice 1 perché on hover viene mostrata la seconda immagine
      cardStates.set(productId, {
        currentIndex: 1,
        totalImages: mediaData.length,
        mediaData: mediaData,
        mediaContainer: mediaContainer,
        slideImg: null
      });

      // Aggiungi attributo per CSS - partiamo da 1 (seconda immagine già visibile on hover)
      nav.setAttribute('data-current-index', '1');

      // Event listeners per le frecce
      const prevBtn = nav.querySelector('.product-card-nav__arrow--prev');
      const nextBtn = nav.querySelector('.product-card-nav__arrow--next');

      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          navigateImage(productId, nav, -1);
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          navigateImage(productId, nav, 1);
        });
      }

      // Reset quando il mouse esce dalla card
      if (productInner) {
        productInner.addEventListener('mouseleave', () => {
          resetToFirstImage(productId, nav);
        });

        // Precarica tutte le immagini quando si entra nella card
        productInner.addEventListener('mouseenter', () => {
          const state = cardStates.get(productId);
          if (state && state.mediaData) {
            // Precarica tutte le immagini del prodotto
            state.mediaData.forEach(img => {
              preloadImage(img.src);
            });
          }
        }, { once: false });
      }
    });
  }

  function navigateImage(productId, nav, direction) {
    const state = cardStates.get(productId);
    if (!state) return;

    const newIndex = state.currentIndex + direction;

    // Check bounds
    if (newIndex < 0 || newIndex >= state.totalImages) return;

    state.currentIndex = newIndex;

    // Aggiorna attributi per CSS
    nav.setAttribute('data-current-index', newIndex.toString());

    // Gestisci classe per ultima immagine
    if (newIndex === state.totalImages - 1) {
      nav.classList.add('at-last-image');
    } else {
      nav.classList.remove('at-last-image');
    }

    // Mostra l'immagine corretta con direzione
    showImage(state, newIndex, direction);
  }

  // Precarica le immagini adiacenti per navigazione più fluida
  function preloadAdjacentImages(state) {
    const { currentIndex, totalImages, mediaData } = state;

    // Precarica immagine precedente e successiva
    const prevIndex = currentIndex - 1;
    const nextIndex = currentIndex + 1;

    if (prevIndex >= 0 && mediaData[prevIndex]) {
      preloadImage(mediaData[prevIndex].src);
    }
    if (nextIndex < totalImages && mediaData[nextIndex]) {
      preloadImage(mediaData[nextIndex].src);
    }
  }

  function showImage(state, index, direction = 1) {
    const { mediaContainer, mediaData } = state;
    if (!mediaContainer || !mediaData[index]) return;

    const imageData = mediaData[index];
    const prevIndex = index - direction;
    const prevImageData = mediaData[prevIndex];

    // Trova o crea la strip contenitore
    let strip = mediaContainer.querySelector('.product-card-slider-strip');

    if (!strip) {
      strip = document.createElement('div');
      strip.className = 'product-card-slider-strip';
      mediaContainer.appendChild(strip);
      state.strip = strip;
    }

    // Attiva lo slider
    mediaContainer.classList.add('slider-active');

    // Funzione per eseguire la transizione dopo il preload
    const executeTransition = () => {
      // Prima transizione (dall'immagine hover alla terza immagine)
      if (!state.isSliderInitialized) {
        state.isSliderInitialized = true;

        // Aggiungi classe sliding per far uscire l'hover
        mediaContainer.classList.add('sliding');

        // Crea le due immagini nella strip: hover (che esce) + nuova (che entra)
        strip.innerHTML = '';

        // Immagine corrente (l'hover, indice 1)
        const currentImg = document.createElement('img');
        currentImg.className = 'product-card-slide-img';
        currentImg.src = mediaData[1].src;
        currentImg.srcset = mediaData[1].srcset;
        currentImg.alt = mediaData[1].alt;
        currentImg.loading = 'eager';

        // Nuova immagine
        const nextImg = document.createElement('img');
        nextImg.className = 'product-card-slide-img';
        nextImg.src = imageData.src;
        nextImg.srcset = imageData.srcset;
        nextImg.alt = imageData.alt;
        nextImg.loading = 'eager';

        if (direction > 0) {
          // Avanti: current | next, poi trasla a sinistra
          strip.appendChild(currentImg);
          strip.appendChild(nextImg);
          strip.style.transform = 'translateX(0)';

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              strip.style.transform = 'translateX(-50%)';

              // Dopo la transizione, rimuovi sliding e prepara per il prossimo
              setTimeout(() => {
                mediaContainer.classList.remove('sliding');
                // Precarica immagini adiacenti
                preloadAdjacentImages(state);
              }, 400);
            });
          });
        } else {
          // Indietro: next | current, parte da -50% e va a 0
          strip.appendChild(nextImg);
          strip.appendChild(currentImg);
          strip.style.transform = 'translateX(-50%)';

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              strip.style.transform = 'translateX(0)';

              setTimeout(() => {
                mediaContainer.classList.remove('sliding');
                preloadAdjacentImages(state);
              }, 400);
            });
          });
        }
      } else {
        // Transizioni successive
        const currentImg = strip.querySelector('.product-card-slide-img:first-child');
        const oldSrc = currentImg ? currentImg.src : '';

        // Prepara la nuova immagine
        const newImg = document.createElement('img');
        newImg.className = 'product-card-slide-img';
        newImg.src = imageData.src;
        newImg.srcset = imageData.srcset;
        newImg.alt = imageData.alt;
        newImg.loading = 'eager';

        // Disabilita temporaneamente la transizione per il reset
        strip.style.transition = 'none';

        if (direction > 0) {
          // Avanti: aggiungi a destra, trasla a sinistra
          strip.innerHTML = '';

          const currImg = document.createElement('img');
          currImg.className = 'product-card-slide-img';
          currImg.src = prevImageData ? prevImageData.src : oldSrc;
          currImg.srcset = prevImageData ? prevImageData.srcset : '';
          currImg.alt = prevImageData ? prevImageData.alt : '';
          currImg.loading = 'eager';

          strip.appendChild(currImg);
          strip.appendChild(newImg);
          strip.style.transform = 'translateX(0)';

          requestAnimationFrame(() => {
            strip.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1)';
            requestAnimationFrame(() => {
              strip.style.transform = 'translateX(-50%)';
              // Precarica immagini adiacenti dopo la transizione
              setTimeout(() => preloadAdjacentImages(state), 400);
            });
          });
        } else {
          // Indietro: aggiungi a sinistra, trasla a destra
          strip.innerHTML = '';

          const currImg = document.createElement('img');
          currImg.className = 'product-card-slide-img';
          currImg.src = prevImageData ? prevImageData.src : oldSrc;
          currImg.srcset = prevImageData ? prevImageData.srcset : '';
          currImg.alt = prevImageData ? prevImageData.alt : '';
          currImg.loading = 'eager';

          strip.appendChild(newImg);
          strip.appendChild(currImg);
          strip.style.transform = 'translateX(-50%)';

          requestAnimationFrame(() => {
            strip.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1)';
            requestAnimationFrame(() => {
              strip.style.transform = 'translateX(0)';
              setTimeout(() => preloadAdjacentImages(state), 400);
            });
          });
        }
      }
    };

    // Precarica l'immagine target prima di eseguire la transizione
    preloadImage(imageData.src).then(() => {
      executeTransition();
    });
  }

  function resetToFirstImage(productId, nav) {
    const state = cardStates.get(productId);
    if (!state) return;

    // Reset a indice 1 (seconda immagine) perché on hover viene mostrata quella
    state.currentIndex = 1;
    nav.setAttribute('data-current-index', '1');
    nav.classList.remove('at-last-image');

    // Rimuovi classi slider-active e sliding
    if (state.mediaContainer) {
      state.mediaContainer.classList.remove('slider-active', 'sliding');

      // Rimuovi la strip
      const strip = state.mediaContainer.querySelector('.product-card-slider-strip');
      if (strip) {
        strip.remove();
      }
    }

    // Reset stato
    state.isSliderInitialized = false;
    state.strip = null;
  }

  // ========== MOBILE TOUCH/SWIPE SUPPORT ==========

  function initMobileSwipe() {
    // Seleziona tutti i media container con JSON (hanno più immagini)
    const mediaContainers = document.querySelectorAll('.product__media');

    mediaContainers.forEach(mediaContainer => {
      if (mediaContainer.dataset.touchInitialized) return;

      // Escludi card dentro caroselli Swiper per evitare conflitti di gesture
      const isInsideSwiper = mediaContainer.closest('.swiper');
      if (isInsideSwiper) {
        mediaContainer.dataset.touchInitialized = 'true';
        return;
      }

      const jsonScript = mediaContainer.querySelector('.product-media-json');
      if (!jsonScript) return;

      let mediaData;
      try {
        mediaData = JSON.parse(jsonScript.textContent);
      } catch (e) {
        return;
      }

      if (mediaData.length < 2) return;

      mediaContainer.dataset.touchInitialized = 'true';

      // Genera un ID unico per mobile se non presente
      const productInner = mediaContainer.closest('.product__inner');
      const productCard = mediaContainer.closest('.product');
      let mobileProductId = 'mobile-' + Math.random().toString(36).substr(2, 9);

      // Prova a usare l'ID dal nav se esiste
      const nav = mediaContainer.querySelector('.product-card-nav');
      if (nav && nav.dataset.productId) {
        mobileProductId = 'mobile-' + nav.dataset.productId;
      }

      // Inizializza stato per mobile (parte da 0 - prima immagine visibile)
      if (!cardStates.has(mobileProductId)) {
        cardStates.set(mobileProductId, {
          currentIndex: 0,
          totalImages: mediaData.length,
          mediaData: mediaData,
          mediaContainer: mediaContainer,
          slideImg: null
        });
      }

      // Variabili per il tracking del touch
      let touchStartX = 0;
      let touchStartY = 0;
      let touchEndX = 0;
      let isSwiping = false;

      mediaContainer.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        isSwiping = true;
      }, { passive: true });

      mediaContainer.addEventListener('touchmove', (e) => {
        if (!isSwiping) return;
        touchEndX = e.touches[0].clientX;

        // Calcola la differenza orizzontale e verticale
        const diffX = Math.abs(touchEndX - touchStartX);
        const diffY = Math.abs(e.touches[0].clientY - touchStartY);

        // Se lo scroll è più verticale che orizzontale, non bloccare
        if (diffY > diffX) {
          isSwiping = false;
        }
      }, { passive: true });

      mediaContainer.addEventListener('touchend', (e) => {
        if (!isSwiping) return;
        isSwiping = false;

        const diffX = touchStartX - touchEndX;

        // Swipe a sinistra (mostra immagine successiva)
        if (diffX > SWIPE_THRESHOLD) {
          navigateImageMobile(mobileProductId, 1);
        }
        // Swipe a destra (mostra immagine precedente)
        else if (diffX < -SWIPE_THRESHOLD) {
          navigateImageMobile(mobileProductId, -1);
        }
      }, { passive: true });
    });
  }

  function navigateImageMobile(productId, direction) {
    const state = cardStates.get(productId);
    if (!state) return;

    const newIndex = state.currentIndex + direction;

    // Check bounds - loop circolare su mobile
    let finalIndex = newIndex;
    if (newIndex < 0) {
      finalIndex = state.totalImages - 1;
    } else if (newIndex >= state.totalImages) {
      finalIndex = 0;
    }

    state.currentIndex = finalIndex;

    // Mostra l'immagine corretta con direzione per effetto slide
    showImage(state, finalIndex, direction);
  }

  // Inizializza quando il DOM è pronto
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initProductCardSlider();
      initMobileSwipe();
    });
  } else {
    initProductCardSlider();
    initMobileSwipe();
  }

  // Re-inizializza quando vengono aggiunti nuovi prodotti (infinite scroll, filtri, etc.)
  const observer = new MutationObserver((mutations) => {
    let shouldInit = false;
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          if (node.querySelector('.product-card-nav') || node.querySelector('.product__media') ||
              node.classList?.contains('product-card-nav') || node.classList?.contains('product__media')) {
            shouldInit = true;
          }
        }
      });
    });
    if (shouldInit) {
      setTimeout(() => {
        initProductCardSlider();
        initMobileSwipe();
      }, 100);
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Esponi funzioni per re-init manuale se necessario
  window.initProductCardSlider = initProductCardSlider;
  window.initMobileSwipe = initMobileSwipe;
})();
